import { useState, useEffect, useRef } from "react";
import { fetchAccountCapacity } from "@/hooks/useAccountCapacity";
import { useLocation, matchPath, useNavigate, useSearchParams } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useWorkspace } from "@/contexts/WorkspaceContext";
import { useAuth } from "@/contexts/AuthContext";
import { supabase } from "@/integrations/supabase/client";
import { SimplifiedConnectStep } from "./SimplifiedConnectStep";
import { SimplifiedCampaignStep } from "./SimplifiedCampaignStep";
import { SimplifiedReviewStep, GuestThankYouView } from "./SimplifiedReviewStep";
import { SimplifiedProvisioningView } from "./SimplifiedProvisioningView";
import { AiSelectBusinessContextStep } from "./AiSelectBusinessContextStep";
import { AiSelectCampaignContextStep } from "./AiSelectCampaignContextStep";
import { MessagesBuilderCard } from "./MessagesBuilderCard";
import { SequenceTimeline } from "./SequenceTimeline";
import { CampaignModeStep } from "./CampaignModeStep";
import { ManagedScheduleStep } from "@/components/managed-campaign/ManagedScheduleStep";
import { ManagedCampaignData, initialManagedCampaignData } from "@/components/managed-campaign/ManagedCampaignData";
import { Card, CardContent } from "@/components/ui/card";
import { toast } from "sonner";
import { nameFromCampaignGoals, dedupeName, LEGACY_DEFAULT_NAMES } from "@/lib/campaignAutoName";
import { Loader2, ArrowLeft, ArrowRight, RotateCcw } from "lucide-react";
import { cn } from "@/lib/utils";

// Validation function to check required fields before submission
const validateCampaignData = (data: ManagedCampaignData, isGuest: boolean): { valid: boolean; errors: string[] } => {
  const errors: string[] = [];
  
  if (!data.clientId) errors.push("Please connect a LinkedIn account");
  
  // Campaign name is auto-generated when empty, so it is never a blocking error.
  
  // Validate based on target acquisition mode
  const targetMode = data.targetAcquisitionMode || "automatic";
  
  if (targetMode === "csv") {
    // CSV mode requires uploaded targets
    if (!data.uploadedTargets || data.uploadedTargets.length === 0) {
      errors.push("Please upload a CSV file with at least one target");
    }
  } else if (targetMode === "sales_nav") {
    // Guests: URL is enough. Authenticated: must have imported targets from search.
    if (isGuest) {
      if (!data.salesNavUrl?.trim()) {
        errors.push("Please provide a Sales Navigator search link");
      }
    } else if (!data.uploadedTargets || data.uploadedTargets.length === 0) {
      errors.push("Please import at least one target from Sales Navigator");
    }
  } else {
    // For automatic mode, require criteria
    if (data.countries.length === 0) errors.push("At least one country is required");
    if (data.industries.length === 0) errors.push("At least one industry is required");
    if (data.companySizes.length === 0) errors.push("At least one company size is required");
    if (data.jobTitles.length === 0) errors.push("At least one job title is required");
  }
  
  if (!data.sequences || data.sequences.length === 0) {
    errors.push("At least one sequence step is required");
  }
  
  return { valid: errors.length === 0, errors };
};

// Generate campaign name based on context
const generateCampaignName = (workspaceName: string, data: ManagedCampaignData): string => {
  const now = new Date();
  const monthNames = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
  const month = monthNames[now.getMonth()];
  
  // Try to create a descriptive name based on available context
  const targetMode = data.targetAcquisitionMode || "automatic";
  
  if (targetMode === "csv") {
    const targetCount = data.uploadedTargets?.length || 0;
    return `${workspaceName} - ${month} Campaign (${targetCount} targets)`;
  }
  
  if (targetMode === "sales_nav") {
    return `${workspaceName} - Sales Nav Campaign ${month}`;
  }
  
  // For automatic mode, use job titles or industries for context
  if (data.jobTitles.length > 0) {
    const primaryTitle = data.jobTitles[0];
    return `${workspaceName} - ${primaryTitle} Outreach ${month}`;
  }
  
  if (data.industries.length > 0) {
    const primaryIndustry = data.industries[0];
    return `${workspaceName} - ${primaryIndustry} ${month}`;
  }
  
  return `${workspaceName} - Campaign ${month} ${now.getFullYear()}`;
};
import SimplifiedCampaigns from "@/components/simplified-workspace/SimplifiedCampaigns";
import SimplifiedCampaignDetails from "@/components/simplified-workspace/SimplifiedCampaignDetails";
import SimplifiedCampaignSettings from "@/components/simplified-workspace/SimplifiedCampaignSettings";
import { Button } from "@/components/ui/button";
import { ManagedCampaignWizard } from "@/components/managed-campaign/ManagedCampaignWizard";
import { HowManagedCampaignsWorkPage } from "@/pages/HowManagedCampaignsWorkPage";
import { buildSequenceRows } from "@/lib/sequenceRows";

const STORAGE_KEY_CAMPAIGN_WIZARD = 'campaign_wizard_state_v1';

export const SimplifiedOnboardingWizard = ({ guestToken, demoMode = false }: { guestToken?: string; demoMode?: boolean }) => {
    const { workspace } = useWorkspace();
    const { user } = useAuth();
    const location = useLocation();
    const queryClient = useQueryClient();
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();

    // Check if AI is enabled for this workspace
    const isAiEnabled = true;

    // Scope storage by guest token (for unique links) or workspace (for internal users)
    const storageKey = guestToken
        ? `campaign_wizard_state_${guestToken}`
        : `campaign_wizard_state_internal_${workspace?.id || 'default'}`;

    const [step, setStep] = useState(() => {
        // Demo mode: always start fresh at step 1
        if (demoMode) return 1;
        try {
            const saved = localStorage.getItem(storageKey);
            return saved ? JSON.parse(saved).step : 1;
        } catch (e) {
            return 1;
        }
    });

    const [data, setData] = useState<ManagedCampaignData>(() => {
        try {
            const saved = localStorage.getItem(storageKey);
            return saved ? JSON.parse(saved).data : {
                ...initialManagedCampaignData,
                campaignName: "", campaignNameAuto: true, targetAcquisitionMode: undefined
            };
        } catch (e) {
            console.error(e);
            return {
                ...initialManagedCampaignData,
                campaignName: "", campaignNameAuto: true, targetAcquisitionMode: undefined
            };
        }
    });

    const [clientName, setClientName] = useState("");
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [guestSubmissionComplete, setGuestSubmissionComplete] = useState(false);

    // Preselect target acquisition mode from ?target= query param (from NewCampaign landing page)
    useEffect(() => {
        if (guestToken || demoMode) return;
        const targetParam = searchParams.get("target");
        if (targetParam === "csv" || targetParam === "sales_nav" || targetParam === "automatic") {
            setData(prev => (prev.targetAcquisitionMode === targetParam ? prev : {
                ...prev,
                targetAcquisitionMode: targetParam,
            }));
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [searchParams, guestToken, demoMode]);

    // Selected mode state (regular or ai) - persisted to localStorage
    // Note: Guests/demo can also choose mode if AI is enabled, so we don't force regular anymore
    const [selectedMode, setSelectedMode] = useState<"regular" | "ai" | null>(() => {
        // Demo mode:
        // - If AI is enabled: start at null to show mode selection
        // - If AI is disabled: start directly in regular mode
        if (demoMode) return "regular";
        try {
            const saved = localStorage.getItem(storageKey);
            return saved ? JSON.parse(saved).selectedMode || null : null;
        } catch (e) {
            return null;
        }
    });

    // Derived: whether we're in AI mode
    const isAiMode = selectedMode === "ai" && isAiEnabled;

    // AI Mode specific state - persist to localStorage
    const [selectedCompanyProfileId, setSelectedCompanyProfileId] = useState<string | null>(() => {
        try {
            const saved = localStorage.getItem(storageKey);
            return saved ? JSON.parse(saved).selectedCompanyProfileId || null : null;
        } catch (e) {
            return null;
        }
    });
    const [selectedCampaignKnowledgeId, setSelectedCampaignKnowledgeId] = useState<string | null>(() => {
        try {
            const saved = localStorage.getItem(storageKey);
            return saved ? JSON.parse(saved).selectedCampaignKnowledgeId || null : null;
        } catch (e) {
            return null;
        }
    });
    const [hasAiGenerated, setHasAiGenerated] = useState(() => {
        try {
            const saved = localStorage.getItem(storageKey);
            return saved ? JSON.parse(saved).hasAiGenerated || false : false;
        } catch (e) {
            return false;
        }
    });
    const [hasAudienceGenerated, setHasAudienceGenerated] = useState(() => {
        try {
            const saved = localStorage.getItem(storageKey);
            return saved ? JSON.parse(saved).hasAudienceGenerated || false : false;
        } catch (e) {
            return false;
        }
    });
    const [hasMessagesGenerated, setHasMessagesGenerated] = useState(() => {
        try {
            const saved = localStorage.getItem(storageKey);
            return saved ? JSON.parse(saved).hasMessagesGenerated || false : false;
        } catch (e) {
            return false;
        }
    });

    // Fetch company profiles to check if any exist (for AI mode recommendation)
    const { data: companyProfiles } = useQuery({
        queryKey: ["company-knowledge-profiles-count", workspace?.id],
        queryFn: async () => {
            const { data, error } = await supabase
                .from("company_knowledge_profiles")
                .select("id")
                .eq("workspace_id", workspace!.id)
                .limit(5);
            if (error) throw error;
            return data;
        },
        enabled: !!workspace?.id && !guestToken && !demoMode,
    });

    // Auto-select regular mode when AI is not enabled (skip mode selection)
    useEffect(() => {
        if (!isAiEnabled && selectedMode === null) {
            setSelectedMode("regular");
        }
    }, [isAiEnabled, selectedMode]);

    // Save state to local storage (including AI mode state)
    useEffect(() => {
        if (data.campaignName || step > 1 || selectedMode) { // Only save if there's actual progress
            localStorage.setItem(storageKey, JSON.stringify({ 
                data, 
                step,
                selectedMode,
                // AI mode state
                selectedCompanyProfileId,
                selectedCampaignKnowledgeId,
                hasAiGenerated,
                hasAudienceGenerated,
                hasMessagesGenerated,
            }));
        }
    }, [data, step, storageKey, selectedMode, selectedCompanyProfileId, selectedCampaignKnowledgeId, hasAiGenerated, hasAudienceGenerated, hasMessagesGenerated]);

    const handleReset = () => {
        if (window.confirm("Are you sure you want to reset all campaign progress? This will clear your target audience and messages.")) {
            setData({
                ...initialManagedCampaignData,
                campaignName: "", campaignNameAuto: true, targetAcquisitionMode: undefined
            });
            setStep(1);
            // Reset mode and AI mode state
            setSelectedMode(null);
            setSelectedCompanyProfileId(null);
            setSelectedCampaignKnowledgeId(null);
            setHasAiGenerated(false);
            setHasAudienceGenerated(false);
            setHasMessagesGenerated(false);
            localStorage.removeItem(storageKey);
            toast.success("Campaign progress reset.");
        }
    };

    // Reset only audience step data
    const handleResetAudience = () => {
        if (window.confirm("Reset target audience? This will clear countries, industries, job titles, and other targeting settings.")) {
            updateData({
                countries: [],
                cities: [],
                industries: [],
                companySizes: [],
                companyKeywords: [],
                personKeywords: [],
                excludeCompanyKeywords: [],
                excludePersonKeywords: [],
                departments: [],
                jobTitles: [],
                seniorityLevels: [],
                targetAcquisitionMode: undefined,
                uploadedTargets: [],
            });
            setHasAudienceGenerated(false);
            // Also clear the saved AI audience, otherwise reopening "Describe" restores the old chips.
            if (selectedCompanyProfileId && selectedCampaignKnowledgeId) {
                const ckId = selectedCampaignKnowledgeId;
                try { localStorage.removeItem(`audience-ctx:${ckId}`); } catch { /* ignore */ }
                queryClient.removeQueries({ queryKey: ["audience-ctx", ckId] });
                const empty = Object.fromEntries(["countries", "cities", "industries", "companySizes", "seniorityLevels", "departments", "jobTitles", "companyKeywords", "personKeywords", "excludeCompanyKeywords", "excludePersonKeywords"].map((k) => [k, []]));
                supabase.functions.invoke("company-profile-builder", {
                    body: { action: "audience_save", workspaceId: guestToken ? null : workspace?.id ?? null, guestToken: guestToken ?? null, profileId: selectedCompanyProfileId, campaignKnowledgeId: ckId, filters: empty },
                }).catch(() => { /* local reset already applied */ });
            }
            toast.success("Target audience reset.");
        }
    };

    // Fresh audience/messages whenever the user moves forward into those steps (AI mode); going back keeps edits.
    const prevStepRef = useRef(step);
    useEffect(() => {
        const prev = prevStepRef.current;
        prevStepRef.current = step;
        if (!isAiMode || step <= prev) return;
        if (step === 4 && prev <= 3) {
            updateData({
                industries: [], companySizes: [], companyKeywords: [], personKeywords: [],
                excludeCompanyKeywords: [], excludePersonKeywords: [], departments: [], jobTitles: [], seniorityLevels: [],
                targetAcquisitionMode: undefined, uploadedTargets: [], salesNavUrl: "",
            });
            setHasAudienceGenerated(false);
            // Countries always come from the chosen campaign strategy, never from leftovers on screen.
            if (selectedCampaignKnowledgeId) {
                supabase.from("campaign_knowledge").select("campaign_goals").eq("id", selectedCampaignKnowledgeId).maybeSingle()
                    .then(({ data: ck }) => {
                        const g: any = ck?.campaign_goals ?? {};
                        const c = g.v2?.confirmed ?? {};
                        const fromList = Array.isArray(c.countries) ? c.countries : [];
                        const fromRegion = String(c.region ?? g.target_countries ?? "").split(",").map((x: string) => x.trim()).filter(Boolean);
                        const countries = (fromList.length ? fromList : fromRegion) as string[];
                        if (countries.length) updateData({ countries, cities: [] });
                    });
            }
        }
        if (step === 5 && prev <= 4) {
            updateData({ sequences: initialManagedCampaignData.sequences });
            setHasMessagesGenerated(false);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [step]);

    // Changing business clears the campaign; a valid campaign of this business is required for Audience and later.
    const prevCompanyRef = useRef(selectedCompanyProfileId);
    useEffect(() => {
        if (prevCompanyRef.current !== selectedCompanyProfileId) {
            if (prevCompanyRef.current) setSelectedCampaignKnowledgeId(null);
            prevCompanyRef.current = selectedCompanyProfileId;
        }
    }, [selectedCompanyProfileId]);
    useEffect(() => {
        if (!isAiMode || demoMode || step < 4) return;
        if (!selectedCampaignKnowledgeId) { setStep(3); return; }
        if (guestToken) return;
        let cancelled = false;
        supabase.from("campaign_knowledge").select("company_profile_id").eq("id", selectedCampaignKnowledgeId).maybeSingle()
            .then(({ data: ck, error }) => {
                if (cancelled || error) return;
                if (!ck || (selectedCompanyProfileId && ck.company_profile_id !== selectedCompanyProfileId)) {
                    setSelectedCampaignKnowledgeId(null);
                    setStep(3);
                }
            });
        return () => { cancelled = true; };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [step, selectedCampaignKnowledgeId, selectedCompanyProfileId, isAiMode]);

    // A business profile is required for Strategy and later; clear stale selections (deleted/draft profiles).
    useEffect(() => {
        if (!isAiMode || guestToken || demoMode || !companyProfiles) return;
        const valid = !!selectedCompanyProfileId && companyProfiles.some((p: any) => p.id === selectedCompanyProfileId);
        if (!valid) {
            if (selectedCompanyProfileId) setSelectedCompanyProfileId(null);
            if (step >= 3) setStep(2);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [companyProfiles, selectedCompanyProfileId, step, isAiMode]);

    // Reset only messaging step data
    const handleResetMessaging = () => {
        if (window.confirm("Reset message sequence? This will clear all messages.")) {
            updateData({
                sequences: initialManagedCampaignData.sequences,
            });
            setHasMessagesGenerated(false);
            toast.success("Message sequence reset.");
        }
    };

    // Force fresh start for Demo Mode
    useEffect(() => {
        if (demoMode) {
            localStorage.removeItem(storageKey);
            setStep(1);
            // Demo link always uses Regular mode only (no AI mode / no mode selection screen)
            setSelectedMode("regular");
            setSelectedCompanyProfileId(null);
            setSelectedCampaignKnowledgeId(null);
            setHasAiGenerated(false);
            setHasAudienceGenerated(false);
            setHasMessagesGenerated(false);
            setData({
                ...initialManagedCampaignData,
                campaignName: "Demo Campaign Strategy"
            });
        }
    }, [demoMode, storageKey, isAiEnabled]);

    // Auto-select regular mode for non-AI workspaces (guests or regular users)
    useEffect(() => {
        if (!demoMode && !selectedMode && !isAiEnabled) {
            setSelectedMode("regular");
        }
    }, [demoMode, selectedMode, isAiEnabled]);

    const updateData = (updates: Partial<ManagedCampaignData>) => {
        setData(prev => ({ ...prev, ...updates }));
    };

    // Auto-name the campaign from the chosen campaign strategy unless the user typed their own name.
    useEffect(() => {
        if (!selectedCampaignKnowledgeId || !workspace?.id) return;
        const canOverwrite = data.campaignNameAuto !== false || LEGACY_DEFAULT_NAMES.includes((data.campaignName || "").trim());
        if (!canOverwrite) return;
        let cancelled = false;
        (async () => {
            const { data: ck } = await supabase.from("campaign_knowledge").select("campaign_goals").eq("id", selectedCampaignKnowledgeId).maybeSingle();
            const base = nameFromCampaignGoals(ck?.campaign_goals);
            if (!base || cancelled) return;
            const { data: existing } = await supabase.from("campaigns").select("name").eq("workspace_id", workspace.id).limit(1000);
            if (cancelled) return;
            const name = dedupeName(base, (existing ?? []).map((c: any) => c.name || ""));
            setData(prev => (prev.campaignNameAuto === false && !LEGACY_DEFAULT_NAMES.includes((prev.campaignName || "").trim())) ? prev : { ...prev, campaignName: name, campaignNameAuto: true });
        })();
        return () => { cancelled = true; };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedCampaignKnowledgeId, workspace?.id]);

    const handleConnectNext = async (clientId: string) => {
        // setData(prev => ({ ...prev, clientId })); // Moved logic down to handle demo mode overrides

        let clientPremium = false;

        try {
            if (guestToken) {
                // Fetch using guest token RPC
                const { data: clients } = await supabase
                    .rpc('get_guest_connected_accounts', { token_uuid: guestToken });

                const client = clients?.find(c => c.id === clientId);
                if (client) {
                    setClientName(client.linkedin_name || client.email);
                    clientPremium = client.is_premium || false;
                }
            } else {
                // Fetch using standard query
                const { data: client } = await supabase
                    .from("clients")
                    .select("linkedin_name, email, is_premium")
                    .eq("id", clientId)
                    .single();

                if (client) {
                    setClientName(client.linkedin_name || client.email);
                    clientPremium = client.is_premium || false;
                }
            }
        } catch (error) {
            console.error('Error fetching client details:', error);
        }

        if (demoMode) {
            // Pre-fill with dummy data for demo
            setData(prev => ({
                ...prev,
                clientId,
                isPremium: clientPremium,
                campaignName: "Demo Campaign Strategy",
                countries: ["United States", "United Kingdom"],
                industries: ["Software Development", "IT Services"],
                companySizes: ["11-50", "51-200"],
                companyKeywords: ["SaaS", "AI", "B2B"],
                departments: ["Business Development", "Sales"],
                jobTitles: ["CEO", "Founder", "CTO"],
                sequences: [
                    {
                        step_number: 1,
                        action_type: "connect",
                        delay_hours: 0,
                        include_message: true,
                        message_template: "Hi {first_name}, I saw you're leading {company}. Would love to connect and share some insights on AI automation."
                    },
                    {
                        step_number: 2,
                        action_type: "message",
                        delay_hours: 24,
                        message_template: "Thanks for connecting! I'm curious how you're currently handling your outbound strategy?"
                    }
                ]
            }));
        } else {
            setData(prev => ({ ...prev, clientId, isPremium: clientPremium }));
        }

        setStep(2);
    };

    const handleSubmit = async () => {
        const isGuestSubmission = !!guestToken || demoMode;
        
        // Validate before proceeding
        const validation = validateCampaignData(data, isGuestSubmission);
        if (!validation.valid) {
            // Show single combined toast instead of stacking multiple
            toast.error(`Please fix the following: ${validation.errors.join(', ')}`);
            return;
        }

        // Auto-generate campaign name for guest submissions
        let finalData = { ...data };
        if (!data.campaignName?.trim()) {
            finalData.campaignName = generateCampaignName(workspace?.name || 'Campaign', data);
        }

        // Append excluded keywords (if any) to additional_notes for backwards-compatible persistence
        const excludeCompany = (finalData.excludeCompanyKeywords || []).filter(Boolean);
        const excludePerson = (finalData.excludePersonKeywords || []).filter(Boolean);
        if (excludeCompany.length > 0 || excludePerson.length > 0) {
            const augmented = [
                finalData.additionalNotes || '',
                excludeCompany.length > 0 ? `[Excluded company keywords: ${excludeCompany.join(', ')}]` : '',
                excludePerson.length > 0 ? `[Excluded person keywords: ${excludePerson.join(', ')}]` : '',
            ].filter(Boolean).join('\n').trim();
            finalData.additionalNotes = augmented;
        }

        if (demoMode) {
            setIsSubmitting(true);
            await new Promise(resolve => setTimeout(resolve, 1500));
            setIsSubmitting(false);
            toast.success("Demo campaign launched!");
            setGuestSubmissionComplete(true);
            return;
        }

        if (!workspace?.id || (!guestToken && !user?.id)) {
            toast.error("Session error. Please refresh.");
            return;
        }

        setIsSubmitting(true);
        try {
            if (guestToken) {
                // Guest Submission
                const { data: requestId, error } = await supabase.rpc('submit_guest_campaign', {
                    token_uuid: guestToken,
                    campaign_data: JSON.parse(JSON.stringify({
                        ...finalData,
                        sequences: finalData.sequences
                    }))
                });
                if (error) throw error;

                // Send email notification to super admins
                try {
                    await supabase.functions.invoke('send-email', {
                        body: {
                            action: 'notify_super_admins',
                            templateSlug: 'managed_campaign_request_notification',
                            variables: {
                                request_id: requestId,
                                workspace_name: workspace?.name || 'Unknown Workspace',
                                campaign_name: finalData.campaignName,
                                client_name: clientName || 'Not assigned',
                                target_countries: finalData.countries.join(', '),
                                target_industries: finalData.industries.join(', '),
                                target_departments: finalData.departments.join(', '),
                                job_titles: finalData.jobTitles.join(', '),
                                seniority_levels: finalData.seniorityLevels.join(', '),
                                max_connections_daily: finalData.maxConnectionsDaily,
                                max_messages_daily: finalData.maxMessagesDaily,
                                additional_notes: finalData.additionalNotes || '',
                                admin_url: `${window.location.origin}/admin/managed-campaigns`,
                            }
                        }
                    });
                } catch (e) {
                    console.error("Failed to send guest submission email:", e);
                }

                toast.success("Campaign request submitted successfully!");
                setGuestSubmissionComplete(true);
            } else {
                // Authenticated Submission
                let targetClientId = data.clientId;

                if (!targetClientId) {
                    console.log("Missing client ID, attempting to recover...");
                    const { data: client } = await supabase
                        .from("clients")
                        .select("id")
                        .eq("workspace_id", workspace.id)
                        .neq("status", "disconnected")
                        .limit(1)
                        .maybeSingle();

                    if (client) {
                        targetClientId = client.id;
                        setData(prev => ({ ...prev, clientId: client.id }));
                    } else {
                        toast.error("No connected LinkedIn account found. Please connect an account.");
                        setStep(1);
                        setIsSubmitting(false);
                        return;
                    }
                }

                // Clamp the new campaign's daily limits to the LinkedIn account's
                // remaining capacity so we never over-allocate the account ceiling.
                // Capacity NEVER blocks submission — the request is reviewed and
                // configured by our team before the campaign is started.
                let effConnections = data.maxConnectionsDaily;
                let effMessages = data.maxMessagesDaily;
                const capacity = await fetchAccountCapacity(targetClientId);
                if (capacity) {
                    effConnections = Math.min(effConnections, capacity.availableConnections);
                    effMessages = Math.min(effMessages, capacity.availableMessages);
                    if (capacity.availableConnections <= 0 && capacity.availableMessages <= 0) {
                        toast.info(
                            `${capacity.accountName}'s daily allowance is currently fully used by its other campaigns. Your request is still submitted — our team will assign daily limits before this campaign starts.`
                        );
                    } else if (effConnections !== data.maxConnectionsDaily || effMessages !== data.maxMessagesDaily) {
                        toast.info(
                            `Daily limits adjusted to fit ${capacity.accountName}'s remaining capacity: ${effConnections} connections / ${effMessages} messages per day.`
                        );
                    }
                }


                const acquisitionMode = data.targetAcquisitionMode || "automatic";
                const isSelfServeMode = acquisitionMode === "csv" || acquisitionMode === "sales_nav";

                const { data: campaign, error: campaignError } = await supabase
                    .from("campaigns")
                    .insert([{
                        user_id: user.id,
                        client_id: targetClientId,
                        name: finalData.campaignName,
                        // CSV/Sales Nav: create as paused so user can review & start.
                        // Automatic (managed): keep as draft for ops team to configure.
                        status: isSelfServeMode ? "paused" : "draft",
                        timezone: data.timezone,
                        working_hours_start: data.workingHoursStart,
                        working_hours_end: data.workingHoursEnd,
                        working_days: data.workingDays,
                        max_connections_daily: effConnections,
                        max_messages_daily: effMessages,

                        respect_working_hours: !(data.workingHoursStart === 0 && data.workingHoursEnd === 24),
                    }])
                    .select("id")
                    .single();

                if (campaignError) throw campaignError;

                const sequencesToInsert = buildSequenceRows(data.sequences, campaign.id, {
                    normalizeVariables: false,
                });

                const { error: sequenceError } = await supabase
                    .from("sequences")
                    .insert(sequencesToInsert);

                if (sequenceError) throw sequenceError;

                // CSV or authenticated Sales Nav: insert uploaded targets directly.
                // Both flows produce data.uploadedTargets locally; targets table RLS permits this insert.
                if (isSelfServeMode && data.uploadedTargets && data.uploadedTargets.length > 0) {
                    const seenKeys = new Set<string>();
                    const dedupedTargets = data.uploadedTargets.filter(t => {
                        const key = (t.profile_url || "").toLowerCase().trim();
                        if (!key || seenKeys.has(key)) return false;
                        seenKeys.add(key);
                        return true;
                    });

                    const { error: targetsError } = await supabase
                        .from("targets")
                        .insert(dedupedTargets.map((target) => ({
                            campaign_id: campaign.id,
                            profile_url: target.profile_url,
                            first_name: target.first_name,
                            last_name: target.last_name,
                            company: target.company,
                            title: target.title,
                            provider_id: target.provider_id,
                            link_type: target.link_type,
                            sales_nav_url: target.sales_nav_url,
                            custom_data: target.custom_data || null,
                        })));

                    if (targetsError) throw targetsError;
                }

                // Managed request creation ONLY for automatic (managed) mode.
                // SKIP for CSV/Sales Nav (self-serve) and Demo Mode.
                if (!demoMode && !isSelfServeMode) {
                    const { error: requestError } = await supabase.from("managed_campaign_requests").insert([{
                        workspace_id: workspace.id,
                        user_id: user.id,
                        campaign_name: finalData.campaignName,
                        client_id: targetClientId,
                        campaign_id: campaign.id,
                        target_countries: data.countries,
                        target_cities: data.cities,
                        target_industries: data.industries,
                        company_sizes: data.companySizes,
                        company_keywords: data.companyKeywords,
                        person_keywords: data.personKeywords,
                        target_departments: data.departments,
                        job_titles: data.jobTitles,
                        seniority_levels: data.seniorityLevels,
                        additional_notes: finalData.additionalNotes,
                        sequence_template: JSON.parse(JSON.stringify(data.sequences)),
                        timezone: data.timezone,
                        working_hours_start: data.workingHoursStart,
                        working_hours_end: data.workingHoursEnd,
                        working_days: data.workingDays,
                        max_connections_daily: effConnections,
                        max_messages_daily: effMessages,

                        status: "pending_setup",
                    }]);

                    if (requestError) throw requestError;

                    try {
                        await supabase.functions.invoke('send-email', {
                            body: {
                                action: 'notify_super_admins',
                                templateSlug: 'managed_campaign_request_notification',
                                variables: {
                                    request_id: campaign.id,
                                    workspace_name: workspace.name || 'Unknown Workspace',
                                    campaign_name: finalData.campaignName,
                                    client_name: clientName || 'Not assigned',
                                    target_countries: data.countries.join(', '),
                                    target_industries: data.industries.join(', '),
                                    target_departments: data.departments.join(', '),
                                    job_titles: data.jobTitles.join(', '),
                                    seniority_levels: data.seniorityLevels.join(', '),
                                    max_connections_daily: data.maxConnectionsDaily,
                                    max_messages_daily: data.maxMessagesDaily,
                                    additional_notes: finalData.additionalNotes || '',
                                    admin_url: `${window.location.origin}/admin/managed-campaigns`,
                                }
                            }
                        });
                    } catch (e) {
                        console.error(e);
                    }
                }

                const successMessage = demoMode
                    ? "Demo campaign created!"
                    : isSelfServeMode
                        ? "Campaign created with your targets. Review and start it from the campaign page."
                        : "Campaign launched! We are setting it up.";
                toast.success(successMessage);
                // Invalidate query to update status
                await queryClient.invalidateQueries({ queryKey: ['simplified-onboarding-status', workspace?.id] });

                // Clear local storage and reset state
                localStorage.removeItem(storageKey);
                setData({
                    ...initialManagedCampaignData,
                    campaignName: "", campaignNameAuto: true, targetAcquisitionMode: undefined
                });
                setStep(1);
                setClientName("");

                // Navigate to the campaigns dashboard
                navigate('/campaigns');
                return; // Exit early after navigation
            }

        } catch (error: any) {
            console.error(error);
            toast.error("Failed to submit campaign request: " + (error.message || "Unknown error"));
        } finally {
            setIsSubmitting(false);
        }
    };



    const isOnboardingPage = matchPath("/campaigns/onboarding", location.pathname) || matchPath("/campaigns/onboarding/demo", location.pathname);

    if (!isOnboardingPage && !guestToken) {
        if (matchPath("/campaigns/managed/how-it-works", location.pathname)) {
            return <HowManagedCampaignsWorkPage />;
        }
        if (matchPath("/campaigns/managed", location.pathname)) {
            return <ManagedCampaignWizard />;
        }
        if (matchPath("/campaigns/:id/settings", location.pathname)) {
            return <SimplifiedCampaignSettings />;
        }
        if (matchPath("/campaigns/:id/sequence", location.pathname)) {
            return <SimplifiedCampaignSettings />;
        }
        if (matchPath("/campaigns/:id/audience", location.pathname)) {
            return <SimplifiedCampaignSettings />;
        }
        if (matchPath("/campaigns/:id", location.pathname)) {
            return <SimplifiedCampaignDetails />;
        }
        return <SimplifiedCampaigns />;
    }

    if ((guestToken || demoMode) && guestSubmissionComplete) {
        return <GuestThankYouView />;
    }

    const isModified =
        data.industries.length > 0 ||
        data.jobTitles.length > 0 ||
        data.countries.length > 0 ||
        data.companySizes.length > 0 ||
        data.companyKeywords.length > 0 ||
        data.departments.length > 0 ||
        data.seniorityLevels.length > 0 ||
        // AI mode progress indicators
        !!selectedCompanyProfileId ||
        !!selectedCampaignKnowledgeId ||
        step > 1 ||
        (data.sequences && (
            data.sequences.length !== 2 ||
            data.sequences[0].include_message === true ||
            (data.sequences[0].message_template && data.sequences[0].message_template.length > 0)
        ));

    return (
        <div className="py-8">
            {isOnboardingPage && !guestToken && !demoMode && (
                <div className="max-w-4xl mx-auto px-4 mb-6">
                    <Button
                        variant="ghost"
                        className="gap-2 pl-0 hover:pl-2 transition-all"
                        onClick={() => {
                            if (selectedMode && isAiEnabled) {
                                setSelectedMode(null);
                            } else {
                                navigate("/campaigns");
                            }
                        }}
                    >
                        <ArrowLeft className="w-4 h-4" />
                        {selectedMode && isAiEnabled ? "Back to Mode Selection" : "Back to Campaigns"}
                    </Button>
                </div>
            )}
            {/* Step Indicator - show once mode is chosen OR when AI is disabled (regular-only flow) */}
            {(selectedMode || !isAiEnabled) && (
                <div className="relative max-w-[600px] mx-auto mb-12 px-4">
                    {/* Global Reset Button - top right above step indicator */}
                    {isModified && (
                        <div className="absolute -top-8 right-4 z-20">
                            <Button
                                onClick={handleReset}
                                variant="ghost"
                                size="sm"
                                className="text-muted-foreground hover:text-destructive hover:bg-destructive/10 gap-1.5"
                            >
                                <RotateCcw className="w-3.5 h-3.5" />
                                Start Over
                            </Button>
                        </div>
                    )}
                    
                    {/* Connecting Line */}
                    <div className="absolute top-5 left-0 right-0 mx-10 h-[1px] bg-gray-200 hidden md:block transform -translate-y-1/2 rounded-full" />

                    <div className="flex items-center justify-between w-full relative z-10">
                        {(isAiMode ? [
                            { id: 1, label: "Connect" },
                            { id: 2, label: "Business" },
                            { id: 3, label: "Strategy" },
                            { id: 4, label: "Audience" },
                            { id: 5, label: "Messages" },
                            { id: 6, label: "Schedule" },
                            { id: 7, label: "Review" }
                        ] : [
                            { id: 1, label: "Connect" },
                            { id: 2, label: "Target Audience" },
                            { id: 3, label: "Messaging" },
                            { id: 4, label: "Schedule" },
                            { id: 5, label: "Review" }
                        ]).map((s) => {
                            // Determine if user can navigate to this step
                            const canNavigate = (() => {
                                if (s.id <= step) return true; // Can always go back
                                
                                const targetMode = data.targetAcquisitionMode || "automatic";
                                const hasUploadedTargets = (data.uploadedTargets?.length || 0) > 0;
                                
                                if (isAiMode) {
                                    // AI mode navigation rules (7 steps)
                                    if (s.id >= 2 && !data.clientId) return false;
                                    if (s.id >= 3 && !selectedCompanyProfileId) return false;
                                    if (s.id >= 4 && !selectedCampaignKnowledgeId) return false;
                                    // Step 5: check audience based on target mode
                                    if (s.id >= 5) {
                                        if (targetMode === "csv" || targetMode === "sales_nav") {
                                            if (!hasUploadedTargets) return false;
                                        } else {
                                            if (data.countries.length === 0 || data.jobTitles.length === 0) return false;
                                        }
                                    }
                                    if (s.id >= 6 && data.sequences.filter(seq => seq.action_type !== 'end').length === 0) return false;
                                } else {
                                    // Regular mode navigation rules (5 steps)
                                    if (s.id >= 2 && !data.clientId) return false;
                                    // Step 3: check audience based on target mode
                                    if (s.id >= 3) {
                                        if (targetMode === "csv" || targetMode === "sales_nav") {
                                            if (!hasUploadedTargets) return false;
                                        } else {
                                            if (data.countries.length === 0 || data.industries.length === 0 || data.companySizes.length === 0 || data.jobTitles.length === 0) return false;
                                        }
                                    }
                                    if (s.id >= 4 && data.sequences.filter(seq => seq.action_type !== 'end').length === 0) return false;
                                }
                                return true;
                            })();

                            return (
                                <div
                                    key={s.id}
                                    className={cn(
                                        "flex flex-col items-center gap-2 group bg-background px-2",
                                        canNavigate ? "cursor-pointer" : "cursor-not-allowed opacity-60"
                                    )}
                                    onClick={() => canNavigate && setStep(s.id)}
                                >
                                    <div className={cn(
                                        "w-10 h-10 rounded-full flex items-center justify-center font-semibold transition-all duration-300 border-2 shadow-sm z-10",
                                        step === s.id
                                            ? "bg-primary border-primary text-primary-foreground scale-110 shadow-glow"
                                            : step > s.id
                                                ? "bg-emerald-600 border-emerald-600 text-white"
                                                : canNavigate
                                                    ? "bg-background border-muted-foreground/20 text-muted-foreground group-hover:border-primary/50 group-hover:text-primary/70"
                                                    : "bg-background border-muted-foreground/20 text-muted-foreground"
                                    )}>
                                        {step > s.id ? (
                                            <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
                                                <polyline points="20 6 9 17 4 12" />
                                            </svg>
                                        ) : (
                                            <span>{s.id}</span>
                                        )}
                                    </div>
                                    <span className={cn(
                                        "text-sm font-medium transition-colors duration-200",
                                        step >= s.id ? "text-foreground" : "text-muted-foreground"
                                    )}>
                                        {s.label}
                                    </span>
                                </div>
                            );
                        })}
                    </div>
                </div>
            )}

            <div className="max-w-4xl mx-auto">
                {/* Mode Selection Step - Show for guests and demo too if AI is enabled */}
                {!selectedMode && isAiEnabled && (
                    <CampaignModeStep
                        selectedMode={selectedMode}
                        onSelectMode={setSelectedMode}
                        onNext={() => {
                            // Mode selected, move to step 1 (Connect)
                            setStep(1);
                        }}
                        onBack={(guestToken || demoMode) ? undefined : () => navigate("/campaigns")}
                        hasCompanyProfiles={(companyProfiles?.length || 0) > 0}
                        isAiEnabled={isAiEnabled}
                    />
                )}

                {/* Step 1: Connect - same for both modes (always show for non-AI workspaces) */}
                {(step === 1 && (selectedMode || !isAiEnabled)) && (
                    <SimplifiedConnectStep
                        onNext={handleConnectNext}
                        onBack={isAiEnabled ? () => setSelectedMode(null) : undefined}
                        initialClientId={data.clientId}
                        guestToken={guestToken}
                        demoMode={demoMode}
                        isOnboarding={true}
                    />
                )}

                {/* AI Mode Steps */}
                {isAiMode ? (
                    <>
                        {step === 2 && workspace?.id && (
                            <AiSelectBusinessContextStep
                                workspaceId={workspace.id}
                                selectedProfileId={selectedCompanyProfileId}
                                onSelect={setSelectedCompanyProfileId}
                                onNext={() => setStep(3)}
                                onBack={() => setStep(1)}
                                guestToken={guestToken}
                            />
                        )}

                        {step === 3 && workspace?.id && selectedCompanyProfileId && (
                            <AiSelectCampaignContextStep
                                workspaceId={workspace.id}
                                companyProfileId={selectedCompanyProfileId}
                                selectedCampaignKnowledgeId={selectedCampaignKnowledgeId}
                                onSelect={setSelectedCampaignKnowledgeId}
                                onNext={() => setStep(4)}
                                onBack={() => setStep(2)}
                                guestToken={guestToken}
                            />
                        )}

                        {/* Step 4: Target Audience */}
                        {step === 4 && selectedCompanyProfileId && selectedCampaignKnowledgeId && (
                            <SimplifiedCampaignStep
                                data={data}
                                updateData={updateData}
                                onNext={() => setStep(5)}
                                onBack={() => setStep(3)}
                                mode="audience"
                                onResetStep={handleResetAudience}
                                hideAiStrategist={true}
                                showTargetAcquisition={true}
                                clientId={data.clientId}
                                clientName={clientName}
                                guestToken={guestToken}
                                aiGenerationProps={{
                                    companyProfileId: selectedCompanyProfileId,
                                    campaignKnowledgeId: selectedCampaignKnowledgeId,
                                    hasGenerated: hasAudienceGenerated,
                                    onGenerated: (generated) => {
                                        updateData(generated);
                                        setHasAudienceGenerated(true);
                                    }
                                }}
                            />
                        )}

                        {/* Step 5: Messages */}
                        {step === 5 && selectedCompanyProfileId && selectedCampaignKnowledgeId && (
                            <div className="space-y-6 animate-in fade-in duration-500">
                                <div className="space-y-1">
                                    <h2 className="text-2xl font-bold tracking-tight">Message Sequence</h2>
                                    <p className="text-muted-foreground">
                                        Create personalized messages for your outreach.
                                    </p>
                                </div>

                                <MessagesBuilderCard
                                    guestToken={guestToken}
                                    workspaceId={workspace?.id ?? null}
                                    companyProfileId={selectedCompanyProfileId}
                                    campaignKnowledgeId={selectedCampaignKnowledgeId}
                                    onGenerated={(generated) => {
                                        updateData(generated);
                                        setHasMessagesGenerated(true);
                                    }}
                                    hasGenerated={hasMessagesGenerated}
                                    isPremium={data.isPremium || false}
                                    currentSequences={data.sequences}
                                    audienceFilters={data as any}
                                />

                                {(hasMessagesGenerated || data.sequences.some((s) => s.action_type !== "end" && s.message_template?.trim())) && (
                                    <SequenceTimeline
                                        sequences={data.sequences}
                                        onChange={(sequences) => updateData({ sequences })}
                                        isPremium={data.isPremium || false}
                                    />
                                )}

                                <div className="flex items-center justify-between pt-2">
                                    <Button variant="outline" onClick={() => setStep(4)}>Back</Button>
                                    <div className="flex gap-2">
                                        <Button variant="ghost" onClick={handleResetMessaging}>Reset</Button>
                                        <Button onClick={() => setStep(6)}>Continue</Button>
                                    </div>
                                </div>
                            </div>
                        )}

                        {/* Step 6: Schedule */}
                        {step === 6 && (
                            <div className="space-y-6 animate-in fade-in duration-500">
                                <div className="space-y-1">
                                    <h2 className="text-2xl font-bold tracking-tight">Campaign Schedule</h2>
                                    <p className="text-muted-foreground">
                                        Choose when your campaign should run — timezone, working hours, and days.
                                    </p>
                                </div>
                                <ManagedScheduleStep data={data} updateData={updateData} />
                                <div className="flex justify-between pt-4">
                                    <Button variant="outline" onClick={() => setStep(5)}>
                                        <ArrowLeft className="w-4 h-4 mr-2" /> Back
                                    </Button>
                                    <Button onClick={() => setStep(7)}>
                                        Next <ArrowRight className="w-4 h-4 ml-2" />
                                    </Button>
                                </div>
                            </div>
                        )}

                        {/* Step 7: Review */}
                        {step === 7 && (
                            <SimplifiedReviewStep
                                data={data}
                                clientName={clientName}
                                onBack={() => setStep(6)}
                                onSubmit={handleSubmit}
                                isSubmitting={isSubmitting}
                                isGuest={!!guestToken || demoMode}
                                onNameChange={(name) => updateData({ campaignName: name, campaignNameAuto: false })}
                            />
                        )}
                    </>
                ) : (selectedMode || guestToken || demoMode) && (
                    <>
                        {/* Regular Mode Steps */}
                        {step === 2 && (
                            <SimplifiedCampaignStep
                                data={data}
                                updateData={updateData}
                                onNext={() => setStep(3)}
                                onBack={() => setStep(1)}
                                mode="audience"
                                onResetStep={handleResetAudience}
                                hideAiStrategist={true}
                                showTargetAcquisition={true}
                                clientId={data.clientId}
                                clientName={clientName}
                                guestToken={guestToken}
                            />
                        )}

                        {step === 3 && (
                            <SimplifiedCampaignStep
                                data={data}
                                updateData={updateData}
                                onNext={() => setStep(4)}
                                onBack={() => setStep(2)}
                                mode="messaging"
                                onResetStep={handleResetMessaging}
                                hideAiStrategist={true}
                            />
                        )}

                        {step === 4 && (
                            <div className="space-y-6 animate-in fade-in duration-500">
                                <div className="space-y-1">
                                    <h2 className="text-2xl font-bold tracking-tight">Campaign Schedule</h2>
                                    <p className="text-muted-foreground">
                                        Choose when your campaign should run — timezone, working hours, and days.
                                    </p>
                                </div>
                                <ManagedScheduleStep data={data} updateData={updateData} />
                                <div className="flex justify-between pt-4">
                                    <Button variant="outline" onClick={() => setStep(3)}>
                                        <ArrowLeft className="w-4 h-4 mr-2" /> Back
                                    </Button>
                                    <Button onClick={() => setStep(5)}>
                                        Next <ArrowRight className="w-4 h-4 ml-2" />
                                    </Button>
                                </div>
                            </div>
                        )}

                        {step === 5 && (
                            <SimplifiedReviewStep
                                data={data}
                                clientName={clientName}
                                onBack={() => setStep(4)}
                                onSubmit={handleSubmit}
                                isSubmitting={isSubmitting}
                                isGuest={!!guestToken || demoMode}
                                onNameChange={(name) => updateData({ campaignName: name, campaignNameAuto: false })}
                            />
                        )}
                    </>
                )}
            </div>
        </div>
    );
};